/**
 * Короткие звуки событий — подключение, микрофон, демонстрация экрана, камера.
 * Синтезируются на лету (без файлов): рендер в OfflineAudioContext → WAV → <audio>,
 * чтобы звук шёл в выбранное устройство вывода (setSinkId), как и голос собеседников.
 *
 * Не «бип»: каждый звук — удар по чему-то деревянному или стеклянному. Обертоны берём
 * негармоничные (у маримбы они на 4 и 9.2 от основного тона, у колокольчика — на 2.76 и 5.4),
 * высокие гасим быстрее низких, в начале даём короткий призвук атаки, и всё это уезжает
 * в небольшую комнату. Синус с плоской громкостью звучит телефонным гудком — здесь его нет.
 */

export type SoundName =
  | 'join' | 'leave' | 'userJoin' | 'userLeave'
  | 'mute' | 'unmute' | 'deafen' | 'undeafen'
  | 'screenStart' | 'screenStop' | 'streamStarted'
  | 'cameraOn' | 'cameraOff'

type Timbre = 'wood' | 'glass'

/** Нота: частота (Гц), момент удара (с) и громкость относительно остальных нот звука. */
interface Note { f: number; t: number; g?: number }

interface Sound {
  notes: Note[]
  timbre: Timbre
  /** Время затухания основного тона, с. */
  decay: number
  /** Сколько «комнаты» подмешать, 0…1. */
  space: number
  /** Итоговая громкость после выравнивания пиков. */
  level: number
}

/** Обертоны: отношение к основному тону, громкость и доля общего затухания. */
const PARTIALS: Record<Timbre, { ratio: number; gain: number; decay: number }[]> = {
  wood: [
    { ratio: 1, gain: 1, decay: 1 },
    { ratio: 3.99, gain: 0.2, decay: 0.42 },
    { ratio: 9.2, gain: 0.05, decay: 0.2 },
  ],
  glass: [
    { ratio: 1, gain: 1, decay: 1 },
    { ratio: 2.76, gain: 0.26, decay: 0.72 },
    { ratio: 5.4, gain: 0.09, decay: 0.45 },
    { ratio: 8.93, gain: 0.03, decay: 0.28 },
  ],
}

// Тесная пентатоника в среднем регистре: любые две ноты звучат согласно, и ничто не режет ухо.
const A3 = 220, D4 = 293.66, E4 = 329.63, G4 = 392, A4 = 440, D5 = 587.33, E5 = 659.25

const SOUNDS: Record<SoundName, Sound> = {
  // свой вход и выход — тёплое дерево, восходящая и нисходящая квинта с октавой
  join: { notes: [{ f: G4 / 2, t: 0 }, { f: D4, t: 0.055 }, { f: G4, t: 0.11 }], timbre: 'wood', decay: 0.7, space: 0.5, level: 0.9 },
  leave: { notes: [{ f: G4, t: 0 }, { f: D4, t: 0.055 }, { f: G4 / 2, t: 0.11 }], timbre: 'wood', decay: 0.7, space: 0.5, level: 0.9 },
  // чужие приходы и уходы звучат тише и короче: они случаются часто
  userJoin: { notes: [{ f: D4, t: 0 }, { f: A4, t: 0.05 }], timbre: 'wood', decay: 0.45, space: 0.4, level: 0.5 },
  userLeave: { notes: [{ f: A4, t: 0 }, { f: D4, t: 0.05 }], timbre: 'wood', decay: 0.45, space: 0.4, level: 0.5 },
  // микрофон жмут десятки раз за вечер — сухо, коротко и без блеска
  mute: { notes: [{ f: A4, t: 0 }, { f: E4, t: 0.04 }], timbre: 'wood', decay: 0.26, space: 0.15, level: 0.65 },
  unmute: { notes: [{ f: E4, t: 0 }, { f: A4, t: 0.04 }], timbre: 'wood', decay: 0.26, space: 0.15, level: 0.65 },
  // «не слышу» — то же движение, но ниже: слышно, что выключили больше
  deafen: { notes: [{ f: E4, t: 0 }, { f: A3, t: 0.05 }], timbre: 'wood', decay: 0.36, space: 0.2, level: 0.7 },
  undeafen: { notes: [{ f: A3, t: 0 }, { f: E4, t: 0.05 }], timbre: 'wood', decay: 0.36, space: 0.2, level: 0.7 },
  // демонстрация экрана — стекло: заметнее, но не резче
  screenStart: { notes: [{ f: D4, t: 0 }, { f: A4, t: 0.05 }, { f: D5, t: 0.1 }], timbre: 'glass', decay: 0.8, space: 0.6, level: 0.8 },
  screenStop: { notes: [{ f: D5, t: 0 }, { f: A4, t: 0.05 }, { f: D4, t: 0.1 }], timbre: 'glass', decay: 0.8, space: 0.6, level: 0.8 },
  streamStarted: { notes: [{ f: A4, t: 0 }, { f: E5, t: 0.06 }], timbre: 'glass', decay: 0.6, space: 0.5, level: 0.45 },
  cameraOn: { notes: [{ f: G4, t: 0 }, { f: D5, t: 0.045 }], timbre: 'glass', decay: 0.5, space: 0.35, level: 0.6 },
  cameraOff: { notes: [{ f: D5, t: 0 }, { f: G4, t: 0.045 }], timbre: 'glass', decay: 0.5, space: 0.35, level: 0.6 },
}

const RATE = 48_000
const urls = new Map<SoundName, Promise<string>>()

/** Шум с фиксированным зерном: звуки должны получаться одинаковыми при каждом запуске. */
function noiseSource(): () => number {
  let seed = 0x9e3779b9
  return () => {
    seed ^= seed << 13
    seed ^= seed >>> 17
    seed ^= seed << 5
    return ((seed >>> 0) / 0x100000000) * 2 - 1
  }
}

/** Хвост небольшой комнаты: тёмный шум, затухающий за полсекунды. */
function room(ctx: OfflineAudioContext): AudioBuffer {
  const n = Math.ceil(0.5 * RATE)
  const buf = ctx.createBuffer(2, n, RATE)
  const rnd = noiseSource()
  for (let ch = 0; ch < 2; ch++) {
    const data = buf.getChannelData(ch)
    let lp = 0
    for (let i = 0; i < n; i++) {
      lp = lp * 0.72 + rnd() * 0.28 // без шипения: комната, а не помехи
      data[i] = lp * (1 - i / n) ** 2.6
    }
  }
  return buf
}

/** Один удар: обертоны с разным затуханием плюс короткий призвук самой атаки. */
function strike(ctx: OfflineAudioContext, out: AudioNode, note: Note, s: Sound, index: number): number {
  const level = 0.5 * (note.g ?? 1)
  const pan = ctx.createStereoPanner()
  pan.pan.value = [-0.12, 0.1, -0.05][index % 3] ?? 0
  pan.connect(out)

  let end = note.t
  for (const p of PARTIALS[s.timbre]) {
    const d = s.decay * p.decay
    const osc = ctx.createOscillator()
    osc.type = 'sine'
    // лёгкий спад высоты в первые миллисекунды — так звучит удар, а не включённый генератор
    osc.frequency.setValueAtTime(note.f * p.ratio * 1.012, note.t)
    osc.frequency.exponentialRampToValueAtTime(note.f * p.ratio, note.t + 0.03)
    const g = ctx.createGain()
    const peak = level * p.gain
    g.gain.setValueAtTime(0, note.t)
    g.gain.linearRampToValueAtTime(peak, note.t + 0.004)
    g.gain.exponentialRampToValueAtTime(peak * 0.0008, note.t + d)
    osc.connect(g).connect(pan)
    osc.start(note.t)
    osc.stop(note.t + d + 0.02)
    end = Math.max(end, note.t + d)
  }

  // призвук атаки: полоса шума длиной несколько миллисекунд
  const n = Math.ceil(0.02 * RATE)
  const buf = ctx.createBuffer(1, n, RATE)
  const data = buf.getChannelData(0)
  const rnd = noiseSource()
  for (let i = 0; i < n; i++) data[i] = rnd() * (1 - i / n) ** 3
  const src = ctx.createBufferSource()
  src.buffer = buf
  const bp = ctx.createBiquadFilter()
  bp.type = 'bandpass'
  bp.frequency.value = note.f * 3
  bp.Q.value = 1.1
  const tick = ctx.createGain()
  tick.gain.value = level * (s.timbre === 'wood' ? 0.22 : 0.12)
  src.connect(bp).connect(tick).connect(pan)
  src.start(note.t)
  return end
}

async function render(s: Sound): Promise<AudioBuffer> {
  const last = Math.max(...s.notes.map((n) => n.t)) + s.decay
  const ctx = new OfflineAudioContext(2, Math.ceil((last + 0.6) * RATE), RATE)
  // общий фильтр верха: убирает жёсткость, оставляя атаку читаемой
  const tone = ctx.createBiquadFilter()
  tone.type = 'lowpass'
  tone.frequency.value = 7000
  tone.Q.value = 0.5
  const dry = ctx.createGain()
  dry.gain.value = 1
  tone.connect(dry).connect(ctx.destination)
  if (s.space > 0) {
    const conv = ctx.createConvolver()
    conv.buffer = room(ctx)
    const wet = ctx.createGain()
    wet.gain.value = 0.2 * s.space
    tone.connect(conv).connect(wet).connect(ctx.destination)
  }
  s.notes.forEach((note, i) => strike(ctx, tone, note, s, i))
  return ctx.startRendering()
}

/** PCM16 WAV из стереобуфера: пики выравниваем, хвост тишины обрезаем. */
function wav(buf: AudioBuffer, level: number): Blob {
  const L = buf.getChannelData(0)
  const R = buf.numberOfChannels > 1 ? buf.getChannelData(1) : L
  let peak = 0
  let end = 0
  for (let i = 0; i < L.length; i++) {
    const v = Math.max(Math.abs(L[i] ?? 0), Math.abs(R[i] ?? 0))
    if (v > peak) peak = v
    if (v > 2e-4) end = i
  }
  const n = Math.min(L.length, end + Math.ceil(0.01 * RATE))
  const k = (peak > 0 ? level / peak : 0)
  const bytes = n * 4
  const out = new DataView(new ArrayBuffer(44 + bytes))
  const str = (o: number, s: string) => {
    for (let i = 0; i < s.length; i++) out.setUint8(o + i, s.charCodeAt(i))
  }
  str(0, 'RIFF')
  out.setUint32(4, 36 + bytes, true)
  str(8, 'WAVE')
  str(12, 'fmt ')
  out.setUint32(16, 16, true)
  out.setUint16(20, 1, true)
  out.setUint16(22, 2, true)
  out.setUint32(24, RATE, true)
  out.setUint32(28, RATE * 4, true)
  out.setUint16(32, 4, true)
  out.setUint16(34, 16, true)
  str(36, 'data')
  out.setUint32(40, bytes, true)
  const fade = Math.ceil(0.008 * RATE)
  for (let i = 0; i < n; i++) {
    const f = i > n - fade ? (n - i) / fade : 1
    out.setInt16(44 + i * 4, Math.max(-1, Math.min(1, (L[i] ?? 0) * k * f)) * 0x7fff, true)
    out.setInt16(46 + i * 4, Math.max(-1, Math.min(1, (R[i] ?? 0) * k * f)) * 0x7fff, true)
  }
  return new Blob([out], { type: 'audio/wav' })
}

/** Готовый WAV одного звука — для предпрослушивания и проверок. */
export async function renderSound(name: SoundName): Promise<Blob> {
  const s = SOUNDS[name]
  return wav(await render(s), s.level)
}

function urlOf(name: SoundName): Promise<string> {
  let p = urls.get(name)
  if (!p) {
    p = renderSound(name).then((b) => URL.createObjectURL(b))
    urls.set(name, p)
  }
  return p
}

/** Проиграть звук; sinkId — устройство вывода из настроек голоса (пусто — системное). Ошибки автовоспроизведения глотаем. */
export async function playSound(name: SoundName, opts: { volume?: number; sinkId?: string | undefined } = {}): Promise<void> {
  try {
    await playUrl(await urlOf(name), { volume: opts.volume ?? 0.6, sinkId: opts.sinkId })
  } catch {
    /* звук не собрался — молча */
  }
}

/**
 * Проиграть файл по адресу в выбранное устройство вывода: так звучат и звуки событий, и прослушивание
 * звуков панели. Громкость элемента — от 0 до 1, больше браузер не даёт. Вернёт элемент, чтобы
 * прослушивание можно было остановить; null — не заиграло (автовоспроизведение, нет устройства).
 */
export async function playUrl(url: string, opts: { volume?: number; sinkId?: string | undefined } = {}): Promise<HTMLAudioElement | null> {
  try {
    const el = new Audio(url)
    el.volume = Math.max(0, Math.min(1, opts.volume ?? 1))
    if (opts.sinkId && 'setSinkId' in el) await (el as HTMLMediaElement & { setSinkId: (id: string) => Promise<void> }).setSinkId(opts.sinkId).catch(() => undefined)
    await el.play()
    return el
  } catch {
    return null
  }
}
